import { getEmployeeBySlug, createSession, endMeetingBeacon, detectCountry } from "./modules/api.js";
import { createVisitorForm } from "./modules/components/visitor_form.js";
import { createConversation } from "./modules/components/conversation.js";
import { initDrag } from "./modules/drag.js";
import { DragIcon } from "./modules/icons.js";
import { createElement } from "./modules/components/dom_utils.js";

// ─── CSS (embedded at build time) ───────────────────────────────────────────

const WIDGET_CSS = `/* ================================================
   Digital Employee Embed Widget — Figma-matched styles
   All rules scoped to #digital-employee-widget
   ================================================ */

#digital-employee-widget {
  --sm-bg: #000000;
  --sm-surface: rgba(255, 255, 255, 0.12);
  --sm-input-bg: rgba(255, 255, 255, 0.04);
  --sm-input-border: rgba(255, 255, 255, 0.08);
  --sm-text: #ffffff;
  --sm-text-muted: rgba(255, 255, 255, 0.2);
  --sm-text-powered: #888888;
  --sm-user-avatar: #008080;
  --sm-glass: rgba(255, 255, 255, 0.16);
  --sm-end-call: rgba(250, 53, 35, 0.24);
  --sm-end-call-hover: rgba(250, 53, 35, 0.4);
  --sm-radius-panel: 16px;
  --sm-radius-card: 12px;
  --sm-radius-input: 8px;
  --sm-font: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

  font-family: var(--sm-font);
  font-size: 14px;
  line-height: 1.5;
  color: var(--sm-text);
  box-sizing: border-box;
}

#digital-employee-widget *,
#digital-employee-widget *::before,
#digital-employee-widget *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* =============== FAB =============== */
#digital-employee-widget .sm-fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6c5ce7, #a855f7);
  color: #fff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 24px rgba(108, 92, 231, 0.5);
  transition: transform 0.2s, box-shadow 0.2s;
  z-index: 999999;
}

#digital-employee-widget .sm-fab:hover {
  transform: scale(1.08);
  box-shadow: 0 6px 28px rgba(108, 92, 231, 0.6);
}

/* =============== Floating overlay =============== */
#digital-employee-widget .sm-floating-overlay {
  position: fixed;
  bottom: 96px;
  right: 24px;
  z-index: 999998;
  touch-action: none;
}

#digital-employee-widget .sm-floating-panel {
  width: 485px;
  height: 379px;
  background: var(--sm-bg);
  border-radius: var(--sm-radius-panel);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.6);
  overflow: visible;
  position: relative;
  display: flex;
  flex-direction: column;
  animation: sm-slide-up 0.25s ease-out;
}

/* Conversation mode: larger panel for two-column layout */
#digital-employee-widget .sm-floating-panel--conversation {
  width: 900px;
  height: 520px;
  max-height: calc(100vh - 120px);
  max-width: calc(100vw - 48px);
}

@keyframes sm-slide-up {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* =============== Drag Handle =============== */
#digital-employee-widget .sm-drag-handle {
  cursor: grab;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

#digital-employee-widget .sm-drag-handle:active {
  cursor: grabbing;
}

#digital-employee-widget .sm-drag-handle-dots {
  color: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
}

/* =============== Inline container =============== */
#digital-employee-widget.sm-widget-inline {
  min-height: 380px;
  width: 100%;
  background: var(--sm-bg);
}

#digital-employee-widget .sm-inline-container {
  background: var(--sm-bg);
  overflow: visible;
  min-height: 380px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* =============== Visitor Form =============== */
#digital-employee-widget .sm-visitor-form {
  padding: 40px 32px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-height: 0;
  justify-content: center;
  position: relative;
}

#digital-employee-widget .sm-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6c5ce7, #a855f7);
  color: #fff;
  font-size: 22px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
}

#digital-employee-widget .sm-employee-name {
  font-size: 18px;
  font-weight: 600;
  color: var(--sm-text);
  margin-bottom: 2px;
}

#digital-employee-widget .sm-employee-title {
  font-size: 12px;
  color: var(--sm-text-muted);
  margin-bottom: 16px;
}

#digital-employee-widget .sm-greeting {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 28px;
  line-height: 1.5;
  max-width: 320px;
}

#digital-employee-widget .sm-input {
  display: block;
  width: 100%;
  max-width: 300px;
  padding: 10px 14px;
  margin-bottom: 12px;
  background: var(--sm-input-bg);
  border: 1px solid var(--sm-input-border);
  border-radius: var(--sm-radius-input);
  color: var(--sm-text);
  font-size: 14px;
  font-family: var(--sm-font);
  outline: none;
  transition: border-color 0.15s;
}

#digital-employee-widget .sm-input::placeholder {
  color: var(--sm-text-muted);
}

#digital-employee-widget .sm-input:focus {
  border-color: rgba(255, 255, 255, 0.24);
}

#digital-employee-widget .sm-form-fields {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-bottom: 4px;
}

#digital-employee-widget .sm-skip-link {
  margin-top: 12px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
  text-decoration: none;
  cursor: pointer;
  transition: color 0.15s;
}

#digital-employee-widget .sm-skip-link:hover {
  color: rgba(255, 255, 255, 0.7);
}

#digital-employee-widget .sm-button {
  display: block;
  width: 100%;
  max-width: 300px;
  padding: 12px;
  margin-top: 4px;
  background: #fff;
  color: #000;
  border: none;
  border-radius: var(--sm-radius-input);
  font-size: 14px;
  font-weight: 600;
  font-family: var(--sm-font);
  cursor: pointer;
  transition: opacity 0.15s;
}

#digital-employee-widget .sm-button:hover:not(:disabled) {
  opacity: 0.9;
}

#digital-employee-widget .sm-button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

#digital-employee-widget .sm-error-text {
  color: #ff6b6b;
  font-size: 13px;
  text-align: center;
  margin: 0 0 8px 0;
}

/* Phone input group */
#digital-employee-widget .sm-phone-group {
  display: flex;
  gap: 8px;
  width: 100%;
  max-width: 300px;
  margin-bottom: 12px;
}

#digital-employee-widget .sm-phone-input {
  flex: 1;
  min-width: 0;
  margin-bottom: 0;
}

/* Country picker (custom searchable dropdown) */
#digital-employee-widget .sm-country-picker {
  position: relative;
  flex-shrink: 0;
}

#digital-employee-widget .sm-country-trigger {
  padding: 10px 24px 10px 10px;
  background: var(--sm-input-bg);
  border: 1px solid var(--sm-input-border);
  border-radius: var(--sm-radius-input);
  color: var(--sm-text);
  font-size: 14px;
  font-family: var(--sm-font);
  cursor: pointer;
  transition: border-color 0.15s;
  white-space: nowrap;
  height: 100%;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Cpath d='M2.5 4l2.5 2.5 2.5-2.5' stroke='%23888' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
}

#digital-employee-widget .sm-country-trigger:hover {
  border-color: rgba(255, 255, 255, 0.24);
}

#digital-employee-widget .sm-country-dropdown {
  position: absolute;
  bottom: calc(100% + 4px);
  left: 0;
  width: 280px;
  max-height: 300px;
  background: var(--sm-bg);
  border: 1px solid var(--sm-input-border);
  border-radius: 10px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  z-index: 100;
  display: flex;
  flex-direction: column-reverse;
  overflow: hidden;
}

#digital-employee-widget .sm-country-search {
  padding: 10px 12px;
  border: none;
  border-top: 1px solid var(--sm-input-border);
  background: transparent;
  color: var(--sm-text);
  font-size: 13px;
  font-family: var(--sm-font);
  outline: none;
  flex-shrink: 0;
}

#digital-employee-widget .sm-country-search::placeholder {
  color: var(--sm-text-muted);
}

#digital-employee-widget .sm-country-list {
  flex: 1;
  overflow-y: auto;
  padding: 4px 0;
}

#digital-employee-widget .sm-country-list::-webkit-scrollbar {
  width: 3px;
}

#digital-employee-widget .sm-country-list::-webkit-scrollbar-track {
  background: transparent;
}

#digital-employee-widget .sm-country-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

#digital-employee-widget .sm-country-option {
  padding: 7px 12px;
  font-size: 13px;
  color: var(--sm-text);
  cursor: pointer;
  transition: background 0.1s;
  font-family: var(--sm-font);
}

#digital-employee-widget .sm-country-option:hover {
  background: var(--sm-surface);
}

#digital-employee-widget .sm-country-option--selected {
  background: var(--sm-surface);
}

#digital-employee-widget .sm-country-divider {
  padding: 8px 12px 4px;
  font-size: 10px;
  font-weight: 600;
  color: var(--sm-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

#digital-employee-widget .sm-country-empty {
  padding: 16px 12px;
  font-size: 13px;
  color: var(--sm-text-muted);
  text-align: center;
}

/* Field-level error text */
#digital-employee-widget .sm-field-error {
  color: #ff6b6b;
  font-size: 12px;
  text-align: left;
  width: 100%;
  max-width: 300px;
  margin: -8px 0 8px 0;
  padding-left: 2px;
}

/* =============== Theme Toggle =============== */
#digital-employee-widget .sm-theme-toggle {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: var(--sm-glass);
  color: var(--sm-text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: background 0.15s, color 0.15s;
  padding: 0;
}

#digital-employee-widget .sm-theme-toggle:hover {
  background: var(--sm-surface);
  color: var(--sm-text);
}

/* =============== Conversation View (Figma-matched) =============== */
#digital-employee-widget .sm-conversation {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: var(--sm-bg);
  border-radius: var(--sm-radius-panel);
  overflow: hidden;
  position: relative;
}

#digital-employee-widget .sm-conversation--fullscreen {
  position: fixed;
  inset: 0;
  z-index: 999999;
  border-radius: 0;
}

/* When browser fullscreen is active, remove all rounded corners */
#digital-employee-widget:fullscreen,
#digital-employee-widget:fullscreen .sm-inline-container,
#digital-employee-widget:fullscreen .sm-conversation {
  border-radius: 0;
}

/* --- Header bar --- */
#digital-employee-widget .sm-conv-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 16px;
  flex-shrink: 0;
}

#digital-employee-widget .sm-conv-header-left {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  padding-left: 8px;
}

#digital-employee-widget .sm-conv-header-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--sm-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

#digital-employee-widget .sm-conv-header-center {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

#digital-employee-widget .sm-conv-header-right {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
}

#digital-employee-widget .sm-conv-header-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;
  padding: 0;
}

#digital-employee-widget .sm-conv-header-btn:hover {
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
}

/* --- Main content area (2-column body) --- */
#digital-employee-widget .sm-conv-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: row;
  gap: 16px;
  padding: 0 8px;
  overflow: hidden;
  border-radius: 12px;
  box-shadow: 0 4px 60px rgba(0, 0, 0, 0.4);
}

/* --- Greeting area (empty slide fallback) --- */
#digital-employee-widget .sm-conv-greeting-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 32px 24px;
  opacity: 0.7;
}

#digital-employee-widget .sm-conv-greeting-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6c5ce7, #a855f7);
  color: #fff;
  font-size: 26px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}

#digital-employee-widget .sm-conv-greeting-name {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 4px;
}

#digital-employee-widget .sm-conv-greeting-hint {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
}

/* Slide content area */
#digital-employee-widget .sm-conv-slide {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  position: relative;
}

/* --- Thinking overlay --- */
#digital-employee-widget .sm-thinking-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  border-radius: var(--sm-radius-card);
  z-index: 10;
}

#digital-employee-widget .sm-thinking-pill {
  color: rgba(255, 255, 255, 0.85);
  font-size: 1.1rem;
  padding: 10px 28px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 40px;
  animation: sm-think-pulse 2.5s ease-in-out infinite;
}

@keyframes sm-think-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

#digital-employee-widget .sm-conv-slide-inner {
  padding: 32px 40px;
  text-align: center;
  max-width: 90%;
  max-height: 100%;
  overflow-y: auto;
}

#digital-employee-widget .sm-conv-slide-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 12px;
}

#digital-employee-widget .sm-conv-slide-video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 12px;
  display: block;
}

#digital-employee-widget .sm-conv-slide-empty {
  opacity: 0.5;
}

#digital-employee-widget .sm-conv-slide-title {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 16px;
  line-height: 1.3;
}

#digital-employee-widget .sm-conv-slide-desc {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.7;
}

/* --- Footer control bar --- */
#digital-employee-widget .sm-conv-footer {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  flex-shrink: 0;
}

#digital-employee-widget .sm-conv-footer-section {
  flex: 1;
  display: flex;
  align-items: center;
}

#digital-employee-widget .sm-conv-footer-left {
  justify-content: flex-start;
}

#digital-employee-widget .sm-conv-footer-center {
  justify-content: center;
  gap: 12px;
}

#digital-employee-widget .sm-conv-footer-right {
  justify-content: flex-end;
}

/* Powered by branding */
#digital-employee-widget .sm-powered {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

#digital-employee-widget .sm-powered-label {
  font-size: 11px;
  color: #888;
  line-height: 1;
}

#digital-employee-widget .sm-powered-brand {
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  line-height: 1;
}

/* Control buttons (circular, reference-matched) */
#digital-employee-widget .sm-conv-ctrl {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, transform 0.15s;
  padding: 0;
}

#digital-employee-widget .sm-conv-ctrl:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

#digital-employee-widget .sm-conv-ctrl--muted {
  background: #dc2626;
}

#digital-employee-widget .sm-conv-ctrl--muted:hover {
  background: #b91c1c;
}

#digital-employee-widget .sm-conv-ctrl--paused {
  background: rgba(255, 191, 0, 0.24);
}

#digital-employee-widget .sm-conv-ctrl--paused:hover {
  background: rgba(255, 191, 0, 0.4);
}

#digital-employee-widget .sm-conv-ctrl--end {
  width: 56px;
  height: 56px;
  background: #dc2626;
}

#digital-employee-widget .sm-conv-ctrl--end:hover {
  background: #b91c1c;
}

/* =============== Transcript Panel =============== */
#digital-employee-widget .sm-transcript-panel {
  width: 308px;
  min-width: 308px;
  display: flex;
  flex-direction: column;
  background: var(--sm-surface);
  border-radius: 12px;
  overflow: hidden;
}

#digital-employee-widget .sm-conversation--fullscreen .sm-transcript-panel {
  width: 420px;
  min-width: 420px;
}

#digital-employee-widget .sm-transcript-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

#digital-employee-widget .sm-transcript-messages::-webkit-scrollbar {
  width: 4px;
}

#digital-employee-widget .sm-transcript-messages::-webkit-scrollbar-track {
  background: transparent;
}

#digital-employee-widget .sm-transcript-messages::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.12);
  border-radius: 2px;
}

/* Transcript rows */
#digital-employee-widget .sm-transcript-row {
  display: flex;
  align-items: flex-start;
}

#digital-employee-widget .sm-transcript-row--user {
  justify-content: flex-end;
}

#digital-employee-widget .sm-transcript-row--assistant {
  justify-content: flex-start;
}

/* User bubble */
#digital-employee-widget .sm-transcript-bubble {
  max-width: 85%;
  padding: 10px 14px;
  border-radius: 18px 18px 4px 18px;
  font-size: 14px;
  line-height: 20px;
  word-wrap: break-word;
}

#digital-employee-widget .sm-transcript-bubble--user {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

/* Assistant text (no bubble) */
#digital-employee-widget .sm-transcript-text {
  font-size: 14px;
  line-height: 22px;
  color: rgba(255, 255, 255, 0.9);
  max-width: 95%;
  white-space: pre-wrap;
  padding: 2px 0;
}

/* Blinking cursor */
#digital-employee-widget .sm-transcript-cursor {
  display: inline-block;
  width: 2px;
  height: 14px;
  background: rgba(255, 255, 255, 0.7);
  margin-left: 2px;
  vertical-align: text-bottom;
  animation: sm-blink 0.8s step-end infinite;
}

@keyframes sm-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

/* Text input bar */
#digital-employee-widget .sm-transcript-input-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid var(--sm-input-border);
  flex-shrink: 0;
  max-height: 60px;
  opacity: 1;
  overflow: hidden;
  transition: max-height 0.25s ease, opacity 0.2s ease, padding 0.25s ease;
}

#digital-employee-widget .sm-transcript-input-bar--hidden {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
  border-top-color: transparent;
}

#digital-employee-widget .sm-transcript-input {
  flex: 1;
  background: var(--sm-input-bg);
  border: 1px solid var(--sm-input-border);
  border-radius: 20px;
  padding: 10px 16px;
  color: var(--sm-text);
  font-size: 14px;
  outline: none;
  font-family: var(--sm-font);
  transition: border-color 0.2s, box-shadow 0.2s;
}

#digital-employee-widget .sm-transcript-input:focus {
  border-color: rgba(168, 85, 247, 0.5);
  box-shadow: 0 0 0 2px rgba(168, 85, 247, 0.15);
}

#digital-employee-widget .sm-transcript-input::placeholder {
  color: var(--sm-text-muted);
}

#digital-employee-widget .sm-transcript-send {
  background: linear-gradient(135deg, #6c5ce7, #a855f7);
  border: none;
  border-radius: 50%;
  color: #fff;
  width: 36px;
  height: 36px;
  min-width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  font-family: var(--sm-font);
  transition: transform 0.15s, opacity 0.15s, box-shadow 0.15s;
  box-shadow: 0 2px 8px rgba(108, 92, 231, 0.3);
}

#digital-employee-widget .sm-transcript-send:hover {
  transform: scale(1.08);
  box-shadow: 0 4px 12px rgba(108, 92, 231, 0.4);
}

#digital-employee-widget .sm-transcript-send:active {
  transform: scale(0.95);
}

/* Suggestion chips */
#digital-employee-widget .sm-suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 6px 8px;
  flex-shrink: 0;
  max-height: 160px;
  opacity: 1;
  overflow: hidden;
  transition: max-height 0.25s ease, opacity 0.2s ease, padding 0.25s ease;
}

#digital-employee-widget .sm-suggestions--hidden {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}

#digital-employee-widget .sm-suggestion-chip {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  padding: 5px 12px;
  color: rgba(255, 255, 255, 0.55);
  font-size: 12px;
  line-height: 1.35;
  cursor: pointer;
  font-family: var(--sm-font);
  text-align: left;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  opacity: 0;
  transform: translateY(4px);
  animation: sm-chip-in 0.25s ease forwards;
}

@keyframes sm-chip-in {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

#digital-employee-widget .sm-suggestion-chip:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.25);
  color: rgba(255, 255, 255, 0.85);
}

#digital-employee-widget .sm-suggestion-chip:active {
  background: rgba(255, 255, 255, 0.1);
}

/* Empty state */
#digital-employee-widget .sm-transcript-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px 16px;
}

#digital-employee-widget .sm-transcript-empty-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6c5ce7, #a855f7);
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}

#digital-employee-widget .sm-transcript-empty-name {
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 4px;
}

#digital-employee-widget .sm-transcript-empty-hint {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.3);
}

/* =============== Chat Panel =============== */
#digital-employee-widget .sm-chat-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: var(--sm-surface);
  border-radius: var(--sm-radius-card);
  overflow: hidden;
  min-width: 0;
}

#digital-employee-widget .sm-chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Scrollbar */
#digital-employee-widget .sm-chat-messages::-webkit-scrollbar {
  width: 4px;
}

#digital-employee-widget .sm-chat-messages::-webkit-scrollbar-track {
  background: transparent;
}

#digital-employee-widget .sm-chat-messages::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.12);
  border-radius: 2px;
}

/* Chat rows */
#digital-employee-widget .sm-chat-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}

#digital-employee-widget .sm-chat-row--user {
  justify-content: flex-end;
}

#digital-employee-widget .sm-chat-row--ai {
  justify-content: flex-start;
}

/* Avatars */
#digital-employee-widget .sm-chat-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #fff;
}

#digital-employee-widget .sm-chat-avatar--ai {
  background: linear-gradient(135deg, #f5c842, #4a90d9);
}

#digital-employee-widget .sm-chat-avatar--user {
  background: var(--sm-user-avatar);
}

/* Bubbles */
#digital-employee-widget .sm-chat-bubble {
  max-width: 75%;
  padding: 8px 12px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.5;
  word-wrap: break-word;
}

#digital-employee-widget .sm-chat-bubble--ai {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.85);
}

#digital-employee-widget .sm-chat-bubble--user {
  background: rgba(255, 255, 255, 0.1);
  color: var(--sm-text);
}

/* Input bar */
#digital-employee-widget .sm-chat-input-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

#digital-employee-widget .sm-chat-input {
  flex: 1;
  padding: 8px 12px;
  background: var(--sm-input-bg);
  border: 1px solid var(--sm-input-border);
  border-radius: var(--sm-radius-input);
  color: var(--sm-text);
  font-size: 13px;
  font-family: var(--sm-font);
  outline: none;
  transition: border-color 0.15s;
}

#digital-employee-widget .sm-chat-input::placeholder {
  color: var(--sm-text-muted);
}

#digital-employee-widget .sm-chat-input:focus {
  border-color: rgba(255, 255, 255, 0.2);
}

#digital-employee-widget .sm-chat-send {
  width: 32px;
  height: 32px;
  border-radius: var(--sm-radius-input);
  background: #fff;
  color: #000;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.15s;
  flex-shrink: 0;
}

#digital-employee-widget .sm-chat-send:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

#digital-employee-widget .sm-chat-send:hover:not(:disabled) {
  opacity: 0.85;
}

/* Scrollbar for slide content */
#digital-employee-widget .sm-conv-slide-inner::-webkit-scrollbar {
  width: 4px;
}

#digital-employee-widget .sm-conv-slide-inner::-webkit-scrollbar-track {
  background: transparent;
}

#digital-employee-widget .sm-conv-slide-inner::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.12);
  border-radius: 2px;
}

/* =============== End Call Dialog =============== */
#digital-employee-widget .sm-dialog-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(27px);
  -webkit-backdrop-filter: blur(27px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

#digital-employee-widget .sm-dialog-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 440px;
  max-width: 90%;
}

#digital-employee-widget .sm-dialog-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 16px;
  text-align: center;
}

#digital-employee-widget .sm-dialog-title {
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  line-height: 24px;
  margin: 0;
  white-space: nowrap;
}

#digital-employee-widget .sm-dialog-desc {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 17px;
  margin: 0;
  white-space: nowrap;
}

#digital-employee-widget .sm-dialog-cta-wrap {
  display: flex;
  justify-content: center;
  padding: 8px 16px 16px;
}

#digital-employee-widget .sm-dialog-cta-btn {
  background: #fff;
  color: #000;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  height: 40px;
  padding: 0 24px;
  font-size: 16px;
  font-weight: 600;
  font-family: var(--sm-font);
  cursor: pointer;
  box-shadow: 0 4px 40px rgba(0, 0, 0, 0.16);
  white-space: nowrap;
  transition: opacity 0.15s;
}

#digital-employee-widget .sm-dialog-cta-btn:hover {
  opacity: 0.9;
}

#digital-employee-widget .sm-dialog-actions {
  position: absolute;
  bottom: 48px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
}

#digital-employee-widget .sm-dialog-action-btn {
  background: transparent;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 15px;
  font-weight: 400;
  font-family: var(--sm-font);
  cursor: pointer;
  white-space: nowrap;
  line-height: 20px;
  transition: background 0.15s;
}

#digital-employee-widget .sm-dialog-action-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* =============== CTA Button =============== */
#digital-employee-widget .sm-cta-btn {
  background: #fff;
  color: #000;
  border: none;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
  white-space: nowrap;
  line-height: 1;
  height: 32px;
}

#digital-employee-widget .sm-cta-btn:hover {
  opacity: 0.85;
}

#digital-employee-widget .sm-cta-btn--slide {
  font-size: 12px;
  padding: 6px 16px;
}

#digital-employee-widget .sm-slide-cta {
  position: absolute;
  bottom: 12px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  pointer-events: none;
}

#digital-employee-widget .sm-slide-cta .sm-cta-btn {
  pointer-events: auto;
}


/* =============== Light Mode =============== */
#digital-employee-widget[data-sm-theme="light"] {
  --sm-bg: #ffffff;
  --sm-surface: rgba(0, 0, 0, 0.06);
  --sm-input-bg: rgba(0, 0, 0, 0.03);
  --sm-input-border: rgba(0, 0, 0, 0.12);
  --sm-text: #1a1a1a;
  --sm-text-muted: rgba(0, 0, 0, 0.35);
  --sm-text-powered: #666666;
  --sm-glass: rgba(0, 0, 0, 0.08);
  --sm-end-call: rgba(250, 53, 35, 0.12);
  --sm-end-call-hover: rgba(250, 53, 35, 0.24);
}

#digital-employee-widget[data-sm-theme="light"] .sm-drag-handle-dots {
  color: rgba(0, 0, 0, 0.2);
}

#digital-employee-widget[data-sm-theme="light"] .sm-floating-panel {
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.15);
}

#digital-employee-widget[data-sm-theme="light"] .sm-greeting {
  color: rgba(0, 0, 0, 0.5);
}

#digital-employee-widget[data-sm-theme="light"] .sm-input:focus {
  border-color: rgba(0, 0, 0, 0.24);
}

#digital-employee-widget[data-sm-theme="light"] .sm-skip-link {
  color: rgba(0, 0, 0, 0.4);
}

#digital-employee-widget[data-sm-theme="light"] .sm-skip-link:hover {
  color: rgba(0, 0, 0, 0.7);
}

#digital-employee-widget[data-sm-theme="light"] .sm-button {
  background: #1a1a1a;
  color: #ffffff;
}

#digital-employee-widget[data-sm-theme="light"] .sm-conv-title {
  color: var(--sm-text);
}

#digital-employee-widget[data-sm-theme="light"] .sm-conv-header-btn {
  background: rgba(0, 0, 0, 0.06);
  color: rgba(0, 0, 0, 0.5);
}

#digital-employee-widget[data-sm-theme="light"] .sm-conv-header-btn:hover {
  background: rgba(0, 0, 0, 0.12);
  color: #1a1a1a;
}

#digital-employee-widget[data-sm-theme="light"] .sm-conv-body {
  box-shadow: none;
}

#digital-employee-widget[data-sm-theme="light"] .sm-conv-greeting-name {
  color: var(--sm-text);
}

#digital-employee-widget[data-sm-theme="light"] .sm-conv-greeting-hint {
  color: rgba(0, 0, 0, 0.4);
}

#digital-employee-widget[data-sm-theme="light"] .sm-conv-slide {
  background: #ffffff;
}

#digital-employee-widget[data-sm-theme="light"] .sm-thinking-overlay {
  background: rgba(255, 255, 255, 0.6);
}

#digital-employee-widget[data-sm-theme="light"] .sm-thinking-pill {
  color: rgba(0, 0, 0, 0.7);
  background: rgba(0, 0, 0, 0.08);
  border-color: rgba(0, 0, 0, 0.1);
}

#digital-employee-widget[data-sm-theme="light"] .sm-conv-slide-title {
  color: var(--sm-text);
}

#digital-employee-widget[data-sm-theme="light"] .sm-conv-slide-desc {
  color: rgba(0, 0, 0, 0.6);
}

#digital-employee-widget[data-sm-theme="light"] .sm-powered-brand {
  color: var(--sm-text);
}

#digital-employee-widget[data-sm-theme="light"] .sm-conv-ctrl {
  background: rgba(0, 0, 0, 0.1);
  color: #1a1a1a;
}

#digital-employee-widget[data-sm-theme="light"] .sm-conv-ctrl:hover {
  background: rgba(0, 0, 0, 0.18);
}

#digital-employee-widget[data-sm-theme="light"] .sm-conv-ctrl--end {
  background: #dc2626;
  color: #ffffff;
}

#digital-employee-widget[data-sm-theme="light"] .sm-conv-ctrl--end:hover {
  background: #b91c1c;
}

#digital-employee-widget[data-sm-theme="light"] .sm-conv-ctrl--muted {
  background: #dc2626;
  color: #ffffff;
}

#digital-employee-widget[data-sm-theme="light"] .sm-conv-ctrl--muted:hover {
  background: #b91c1c;
}

#digital-employee-widget[data-sm-theme="light"] .sm-transcript-bubble--user {
  background: rgba(0, 0, 0, 0.06);
  color: var(--sm-text);
}

#digital-employee-widget[data-sm-theme="light"] .sm-transcript-text {
  color: var(--sm-text);
}

#digital-employee-widget[data-sm-theme="light"] .sm-transcript-cursor {
  background: rgba(0, 0, 0, 0.5);
}

#digital-employee-widget[data-sm-theme="light"] .sm-transcript-input {
  background: rgba(0, 0, 0, 0.03);
  border-color: rgba(0, 0, 0, 0.12);
  color: var(--sm-text);
}

#digital-employee-widget[data-sm-theme="light"] .sm-transcript-input:focus {
  border-color: rgba(108, 92, 231, 0.5);
  box-shadow: 0 0 0 2px rgba(108, 92, 231, 0.1);
}

#digital-employee-widget[data-sm-theme="light"] .sm-transcript-input::placeholder {
  color: rgba(0, 0, 0, 0.3);
}

#digital-employee-widget[data-sm-theme="light"] .sm-transcript-input-bar {
  border-top-color: rgba(0, 0, 0, 0.08);
}

#digital-employee-widget[data-sm-theme="light"] .sm-transcript-send {
  box-shadow: 0 2px 8px rgba(108, 92, 231, 0.2);
}

#digital-employee-widget[data-sm-theme="light"] .sm-suggestion-chip {
  background: transparent;
  border-color: rgba(0, 0, 0, 0.12);
  color: rgba(0, 0, 0, 0.45);
}

#digital-employee-widget[data-sm-theme="light"] .sm-suggestion-chip:hover {
  background: rgba(0, 0, 0, 0.03);
  border-color: rgba(0, 0, 0, 0.25);
  color: rgba(0, 0, 0, 0.7);
}

#digital-employee-widget[data-sm-theme="light"] .sm-suggestion-chip:active {
  background: rgba(0, 0, 0, 0.05);
}

#digital-employee-widget[data-sm-theme="light"] .sm-transcript-empty-name {
  color: var(--sm-text);
}

#digital-employee-widget[data-sm-theme="light"] .sm-transcript-empty-hint {
  color: rgba(0, 0, 0, 0.3);
}

#digital-employee-widget[data-sm-theme="light"] .sm-chat-bubble--ai {
  background: rgba(0, 0, 0, 0.04);
  color: rgba(0, 0, 0, 0.85);
}

#digital-employee-widget[data-sm-theme="light"] .sm-chat-bubble--user {
  background: rgba(0, 0, 0, 0.08);
}

#digital-employee-widget[data-sm-theme="light"] .sm-chat-input-bar {
  border-top-color: rgba(0, 0, 0, 0.08);
}

#digital-employee-widget[data-sm-theme="light"] .sm-chat-input:focus {
  border-color: rgba(0, 0, 0, 0.2);
}

#digital-employee-widget[data-sm-theme="light"] .sm-chat-send {
  background: #1a1a1a;
  color: #ffffff;
}

#digital-employee-widget[data-sm-theme="light"] .sm-transcript-messages::-webkit-scrollbar-thumb,
#digital-employee-widget[data-sm-theme="light"] .sm-chat-messages::-webkit-scrollbar-thumb,
#digital-employee-widget[data-sm-theme="light"] .sm-conv-slide-inner::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.15);
}

#digital-employee-widget[data-sm-theme="light"] .sm-dialog-overlay {
  background: rgba(0, 0, 0, 0.4);
}

#digital-employee-widget[data-sm-theme="light"] .sm-cta-btn {
  background: #000;
  color: #fff;
}

#digital-employee-widget[data-sm-theme="light"] .sm-country-trigger:hover {
  border-color: rgba(0, 0, 0, 0.24);
}

#digital-employee-widget[data-sm-theme="light"] .sm-country-dropdown {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

#digital-employee-widget[data-sm-theme="light"] .sm-country-list::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.15);
}

#digital-employee-widget[data-sm-theme="light"] .sm-theme-toggle:hover {
  background: rgba(0, 0, 0, 0.1);
}

/* =============== Mobile Layout =============== */
@media (max-width: 768px) {
  /* Full-screen conversation panel on mobile */
  #digital-employee-widget .sm-floating-panel--conversation {
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    max-width: 100vw;
    max-height: 100vh;
    max-height: 100dvh;
    border-radius: 0;
  }

  #digital-employee-widget .sm-floating-overlay {
    bottom: 0;
    right: 0;
    top: 0;
    left: 0;
  }

  /* Body: flip to column — slides on top, transcript below */
  #digital-employee-widget .sm-conv-body {
    flex-direction: column;
    gap: 0;
    padding: 0;
    border-radius: 0;
  }

  /* Slides area: fixed at top, compact height */
  #digital-employee-widget .sm-conv-slide {
    flex: none;
    height: 25vh;
    height: 25dvh;
    border-radius: 0;
    order: -1;
  }

  /* Transcript panel: fills remaining space */
  #digital-employee-widget .sm-transcript-panel {
    width: 100%;
    min-width: 100%;
    flex: 1;
    min-height: 0;
    border-radius: 0;
  }

  #digital-employee-widget .sm-conversation--fullscreen .sm-transcript-panel {
    width: 100%;
    min-width: 100%;
  }

  /* Compact header on mobile */
  #digital-employee-widget .sm-conv-header {
    padding: 6px 12px;
  }

  #digital-employee-widget .sm-conv-title {
    font-size: 14px;
  }

  /* Compact footer on mobile */
  #digital-employee-widget .sm-conv-footer {
    padding: 8px 12px;
  }

  #digital-employee-widget .sm-conv-ctrl {
    width: 40px;
    height: 40px;
  }

  #digital-employee-widget .sm-conv-ctrl--end {
    width: 48px;
    height: 48px;
  }

  /* Hide powered-by text on mobile to save space */
  #digital-employee-widget .sm-powered-label {
    font-size: 10px;
  }

  #digital-employee-widget .sm-powered-brand {
    font-size: 11px;
  }

  /* Smaller greeting avatar on mobile slide area */
  #digital-employee-widget .sm-conv-greeting-area {
    padding: 12px;
  }

  #digital-employee-widget .sm-conv-greeting-avatar {
    width: 40px;
    height: 40px;
    font-size: 18px;
    margin-bottom: 8px;
  }

  #digital-employee-widget .sm-conv-greeting-name {
    font-size: 14px;
  }

  #digital-employee-widget .sm-conv-greeting-hint {
    font-size: 12px;
  }

  /* Inline container also goes full height */
  #digital-employee-widget .sm-inline-container {
    height: 100vh;
    height: 100dvh;
    border-radius: 0;
  }

  /* Form panel: fit mobile viewport */
  #digital-employee-widget .sm-floating-panel {
    width: calc(100vw - 32px);
    max-width: calc(100vw - 32px);
    max-height: 90dvh;
  }

  /* Ensure touch-friendly targets for phone input */
  #digital-employee-widget .sm-country-trigger,
  #digital-employee-widget .sm-phone-input {
    min-height: 44px;
  }

  /* Country dropdown: full width of form fields on mobile */
  #digital-employee-widget .sm-country-dropdown {
    width: calc(100vw - 80px);
    max-width: 300px;
  }
}`;

// ─── Handler ────────────────────────────────────────────────────────────────

const FONT_URL = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap";

const FAB_OPEN_SVG = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 18L18 6M6 6l12 12"/></svg>';
const FAB_CLOSED_SVG = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 10l-4 4l6 6l4-16l-18 7l4 2l2 6l3-4"/></svg>';

export const DigitalEmployeeHandler = {
  // --- State ---
  _state: {
    employee: null,
    step: "form",
    sessionId: null,
    meetingId: null,
    livekitUrl: null,
    token: null,
    roomName: null,
    avatarEnabled: false,
    avatarId: null,
    loading: false,
    detectedCountry: null,
  },
  _slug: null,
  _type: "floating",
  _container: null,
  _audioCtx: null,
  _initError: null,
  _sessionError: null,

  // Floating mode state
  _open: false,
  _fabEl: null,
  _overlayEl: null,
  _panelEl: null,
  _contentEl: null, // Where form/conversation is mounted
  _drag: null,

  // Current component instances
  _formInstance: null,
  _conversationInstance: null,

  // Theme state
  _currentTheme: "light",

  // --- Init ---

  async init(slug, type, topicKey) {
    this._slug = slug;
    this._type = type || "floating";
    this._topicKey = topicKey || null;
    this._initialMessage = null;

    // Inject CSS
    const styleEl = document.createElement("style");
    styleEl.textContent = WIDGET_CSS;
    document.head.appendChild(styleEl);

    // Inject Inter font
    const fontLink = document.createElement("link");
    fontLink.rel = "stylesheet";
    fontLink.href = FONT_URL;
    document.head.appendChild(fontLink);

    // Create container
    this._container = document.getElementById("digital-employee-widget");
    if (!this._container) {
      // Should have been created by entry point
      return;
    }

    // Apply default theme (light)
    this._setTheme("light");

    // Page unload handler
    this._handlePageUnload = () => {
      const s = this._state;
      if (s.meetingId && s.sessionId && s.step === "conversation") {
        endMeetingBeacon(s.meetingId, s.sessionId);
      }
    };
    window.addEventListener("pagehide", this._handlePageUnload);

    // Load employee data and detect country in parallel
    const [employeeResult, countryResult] = await Promise.allSettled([
      getEmployeeBySlug(slug),
      detectCountry(),
    ]);

    if (employeeResult.status === "rejected") {
      console.error("Failed to load employee:", employeeResult.reason);
      this._initError = employeeResult.reason?.message || "Failed to load employee";
      this._renderError();
      return;
    }

    this._state.employee = employeeResult.value;
    this._state.detectedCountry = countryResult.status === "fulfilled" ? countryResult.value : null;

    // Resolve topic key against employee query_params mapping (case-insensitive)
    if (this._topicKey) {
      const mapping = this._state.employee.query_params || {};
      const lowerKey = this._topicKey.toLowerCase();
      const matchedKey = Object.keys(mapping).find(k => k.toLowerCase() === lowerKey);
      this._initialMessage = matchedKey ? mapping[matchedKey] : null;
    }

    // Build the widget
    if (this._type === "floating") {
      this._buildFloatingWidget();
    } else {
      this._buildInlineWidget();
    }
  },

  // --- Error display ---

  _renderError() {
    if (!this._container) return;
    this._container.innerHTML = "";
    const errorDiv = createElement("div", {
      style: "color: #ff6b6b; padding: 40px; text-align: center; font-family: system-ui; background: var(--sm-bg, #fff); min-height: 200px; display: flex; flex-direction: column; align-items: center; justify-content: center; border-radius: 16px;",
    });
    errorDiv.appendChild(createElement("p", { style: "font-size: 16px; margin-bottom: 8px;" }, "Failed to load employee"));
    errorDiv.appendChild(createElement("p", { style: "font-size: 13px; color: rgba(0,0,0,0.4);" }, this._initError));
    errorDiv.appendChild(
      createElement("p", { style: "font-size: 12px; color: rgba(0,0,0,0.25); margin-top: 12px;" }, `Slug: "${this._slug}"`),
    );
    this._container.appendChild(errorDiv);
  },

  // --- Theme ---

  _setTheme(theme) {
    if (!this._container) return;
    this._currentTheme = theme || "light";
    this._container.setAttribute("data-sm-theme", this._currentTheme);
  },

  _toggleTheme() {
    const newTheme = this._currentTheme === "light" ? "dark" : "light";
    this._setTheme(newTheme);
    return newTheme;
  },

  _getTheme() {
    return this._currentTheme || "light";
  },

  // --- Floating widget ---

  _buildFloatingWidget() {
    // FAB button
    this._fabEl = createElement("button", { className: "sm-fab" });
    this._fabEl.setAttribute("aria-label", "Open Digital Employee");
    this._fabEl.innerHTML = FAB_CLOSED_SVG;
    this._fabEl.addEventListener("click", () => this._toggleOpen());
    this._container.appendChild(this._fabEl);

    // Overlay + panel (hidden initially)
    this._overlayEl = createElement("div", { className: "sm-floating-overlay" });
    this._overlayEl.style.display = "none";

    this._panelEl = createElement("div", { className: "sm-floating-panel" });

    // Drag handle
    const dragHandle = createElement("div", { className: "sm-drag-handle" });
    const dragDots = createElement("div", { className: "sm-drag-handle-dots" });
    dragDots.innerHTML = DragIcon;
    dragHandle.appendChild(dragDots);
    this._panelEl.appendChild(dragHandle);

    // Content area (form or conversation goes here)
    this._contentEl = createElement("div", {
      style: "display: flex; flex-direction: column; flex: 1; min-height: 0;",
    });
    this._panelEl.appendChild(this._contentEl);

    this._overlayEl.appendChild(this._panelEl);
    this._container.appendChild(this._overlayEl);

    // Init drag
    this._drag = initDrag(this._overlayEl, dragHandle);

    // Render current step
    this._renderCurrentStep();
  },

  _toggleOpen() {
    this._open = !this._open;
    if (this._open) {
      this._overlayEl.style.display = "";
      this._fabEl.innerHTML = FAB_OPEN_SVG;
    } else {
      this._overlayEl.style.display = "none";
      this._fabEl.innerHTML = FAB_CLOSED_SVG;
      if (this._drag) this._drag.resetPosition();
    }
  },

  // --- Inline widget ---

  _buildInlineWidget() {
    this._contentEl = createElement("div", { className: "sm-inline-container" });
    this._container.appendChild(this._contentEl);
    this._renderCurrentStep();
  },

  // --- Step rendering ---

  _renderCurrentStep() {
    // Cleanup old instances
    this._destroyCurrentView();

    if (!this._contentEl) return;
    this._contentEl.innerHTML = "";

    if (this._state.step === "form" && this._state.employee) {
      if (this._state.employee.employee_type === "onboarding") {
        this._renderOnboardingStart();
      } else {
        this._renderForm();
      }
    } else if (this._state.step === "conversation") {
      this._renderConversation();
    }
  },

  _renderForm() {
    this._formInstance = createVisitorForm({
      employee: this._state.employee,
      detectedCountry: this._state.detectedCountry,
      onSubmit: (info) => this._handleSubmit(info),
      onThemeToggle: () => this._toggleTheme(),
      getTheme: () => this._getTheme(),
    });
    this._contentEl.appendChild(this._formInstance.el);

    // Update panel size for form mode (floating)
    if (this._type === "floating" && this._panelEl) {
      this._panelEl.classList.remove("sm-floating-panel--conversation");
    }
  },

  _renderOnboardingStart() {
    const employee = this._state.employee;
    const wrapper = createElement("div", { className: "sm-visitor-form" });

    // Avatar
    wrapper.appendChild(
      createElement("div", { className: "sm-avatar" }, employee.name.charAt(0)),
    );

    // Name
    wrapper.appendChild(
      createElement("h3", { className: "sm-employee-name" }, employee.name),
    );

    // Error text (hidden initially)
    const errorEl = createElement("p", { className: "sm-error-text" });
    errorEl.style.display = "none";
    wrapper.appendChild(errorEl);

    // Start button
    const button = createElement("button", { className: "sm-button" }, "Start Conversation");
    button.addEventListener("click", () => {
      this._handleSubmit({ name: "Guest", email: "", phone: "" });
    });
    wrapper.appendChild(button);

    // Expose update method so _handleSubmit can show loading/error
    this._formInstance = {
      el: wrapper,
      update({ loading, error }) {
        button.textContent = loading ? "Connecting..." : "Start Conversation";
        button.disabled = loading;
        if (error) {
          errorEl.textContent = error;
          errorEl.style.display = "";
        } else {
          errorEl.style.display = "none";
        }
      },
      destroy() {
        wrapper.remove();
      },
    };

    this._contentEl.appendChild(wrapper);

    // Update panel size for form mode (floating)
    if (this._type === "floating" && this._panelEl) {
      this._panelEl.classList.remove("sm-floating-panel--conversation");
    }
  },

  _renderConversation() {
    // Re-resolve topic key each time a conversation starts (case-insensitive)
    let initialMessage = null;
    if (this._topicKey) {
      const mapping = this._state.employee?.query_params || {};
      const lowerKey = this._topicKey.toLowerCase();
      const matchedKey = Object.keys(mapping).find(k => k.toLowerCase() === lowerKey);
      initialMessage = matchedKey ? mapping[matchedKey] : null;
    }

    this._conversationInstance = createConversation({
      employee: this._state.employee,
      sessionId: this._state.sessionId,
      meetingId: this._state.meetingId,
      livekitUrl: this._state.livekitUrl,
      token: this._state.token,
      avatarEnabled: this._state.avatarEnabled,
      avatarId: this._state.avatarId,
      contentCards: this._state.employee?.content_cards || [],
      audioCtx: this._audioCtx,
      onEnd: () => this._handleEnd(),
      onThemeToggle: () => this._toggleTheme(),
      getTheme: () => this._getTheme(),
      initialMessage: initialMessage,
    });
    this._contentEl.appendChild(this._conversationInstance.el);

    // Update panel size for conversation mode (floating)
    if (this._type === "floating" && this._panelEl) {
      this._panelEl.classList.add("sm-floating-panel--conversation");
      if (this._drag) this._drag.resetPosition();
    }
  },

  _destroyCurrentView() {
    if (this._formInstance) {
      this._formInstance.destroy();
      this._formInstance = null;
    }
    if (this._conversationInstance) {
      this._conversationInstance.destroy();
      this._conversationInstance = null;
    }
  },

  // --- Actions ---

  async _handleSubmit(info) {
    this._state.loading = true;
    this._sessionError = null;
    if (this._formInstance) {
      this._formInstance.update({ loading: true, error: null });
    }

    try {
      // Create AudioContext during user gesture
      if (!this._audioCtx) {
        this._audioCtx = new AudioContext();
        console.log("AudioContext created during user gesture, state:", this._audioCtx.state);
      }
      if (this._audioCtx.state === "suspended") {
        await this._audioCtx.resume();
      }
      const session = await createSession(this._slug, info);
      this._state.loading = false;
      this._state.step = "conversation";
      this._state.sessionId = session.sessionId;
      this._state.meetingId = session.meetingId;
      this._state.livekitUrl = session.livekitUrl;
      this._state.token = session.token;
      this._state.roomName = session.roomName;
      this._state.avatarEnabled = !!session.avatarEnabled;
      this._state.avatarId = session.avatarId ?? null;

      this._postStepMessage("conversation");
      this._renderCurrentStep();
    } catch (err) {
      this._state.loading = false;
      this._sessionError = err.message || "Failed to start conversation. Please try again.";
      if (this._formInstance) {
        this._formInstance.update({ loading: false, error: this._sessionError });
      }
    }
  },

  _handleEnd() {
    this._state.step = "form";
    this._state.sessionId = null;
    this._state.meetingId = null;
    this._state.livekitUrl = null;
    this._state.token = null;
    this._state.roomName = null;
    this._state.avatarEnabled = false;
    this._state.avatarId = null;

    // Close floating panel
    if (this._type === "floating" && this._open) {
      this._toggleOpen();
    }

    this._postStepMessage("form");
    this._renderCurrentStep();
  },

  _postStepMessage(step) {
    const targetOrigin = window.__DIGITAL_EMPLOYEE_API_URL__
      ? new URL(window.__DIGITAL_EMPLOYEE_API_URL__).origin
      : window.location.origin;
    window.parent?.postMessage({ type: "digital-employee-step", step }, targetOrigin);
  },

  // --- Cleanup ---

  destroy() {
    window.removeEventListener("pagehide", this._handlePageUnload);
    this._destroyCurrentView();
    if (this._drag) {
      this._drag.destroy();
      this._drag = null;
    }
    if (this._container) {
      this._container.innerHTML = "";
    }
  },
};
